import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Trash, Eye, ChatCircle, Heart, Warning, ArrowsDownUp } from '@phosphor-icons/react';
import { LineChart, Line, XAxis, YAxis, Tooltip as RTooltip, ResponsiveContainer, CartesianGrid, Legend } from 'recharts';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Md } from '@/components/Md';
import { fmtDate as fmtDatePt } from '@/utils/dateFmt';

const API = process.env.REACT_APP_BACKEND_URL;

// ─── DIARIO TAB (patient diary viewer) ───
const SYMPTOM_LABELS = {
  nauseas: 'Náuseas', enfartamento: 'Enfartamento', refluxo: 'Refluxo',
  inchaço: 'Inchaço', dor_abdominal: 'Dor abdominal', diarreia: 'Diarreia',
  obstipacao: 'Obstipação', gases: 'Gases', reacao_alergica: 'Reação alérgica',
  outro: 'Outro',
};
const BABY_REACTION_LABELS = { aceitou_bem: 'Aceitou bem', recusou: 'Recusou', reacao_alergica: 'Reação alérgica' };
const TEXTURE_LABELS = { liquido: 'Líquido', pure: 'Puré', pedacos: 'Pedaços' };
const ADH_BG = { logged: 'bg-brand-green/10 border-brand-green/30', skipped: 'bg-amber-50 border-amber-300', missing: 'bg-red-50 border-red-200' };
const ADH_DOT = { logged: 'bg-brand-green', skipped: 'bg-amber-400', missing: 'bg-red-400' };
const ADH_LABEL = { logged: 'Registado', skipped: 'Saltou', missing: 'Sem registo' };
const MOOD_EMOJI = ['😞','🙁','😐','🙂','😄'];
const COMPLIANCE_LABEL = { cumpri: 'Cumpri', parcial: 'Parcial', nao_cumpri: 'Não cumpri' };
const COMPLIANCE_DOT = { cumpri: 'bg-brand-green', parcial: 'bg-amber-400', nao_cumpri: 'bg-red-400' };
const COMPLIANCE_PILL = { cumpri: 'bg-brand-green/10 text-brand-green', parcial: 'bg-amber-50 text-amber-700', nao_cumpri: 'bg-red-50 text-red-600' };

function DiárioTab({ patientId }) {
  const [entries, setEntries] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [calendar, setCalendar] = useState({});
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [recordType, setRecordType] = useState('');
  const [validated, setValidated] = useState('');
  const [calMonth, setCalMonth] = useState(new Date().toISOString().slice(0, 7));
  const [view, setView] = useState('plano'); // plano | timeline | calendar

  // Plano vs Diário (Session 6B) — comparison + stats
  const today = new Date().toISOString().slice(0, 10);
  const [planDate, setPlanDate] = useState(today);
  const [comparison, setComparison] = useState(null);
  const [stats, setStats] = useState(null);
  const [statsDays, setStatsDays] = useState(14);
  const [complianceSummary, setComplianceSummary] = useState(null);

  const fetchTimeline = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);
      if (recordType) params.set('record_type', recordType);
      if (validated) params.set('validated', validated);
      const { data } = await axios.get(`${API}/api/patients/${patientId}/timeline?${params}`);
      setTimeline(data);
    } catch {}
  }, [patientId, dateFrom, dateTo, recordType, validated]);

  const fetchCalendar = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/api/patients/${patientId}/adherence-calendar?month=${calMonth}`); setCalendar(data); } catch {}
  }, [patientId, calMonth]);

  const fetchDiary = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/api/patients/${patientId}/diary`); setEntries(data); } catch {}
  }, [patientId]);

  const fetchComparison = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/api/patients/${patientId}/diary-comparison?date=${planDate}`); setComparison(data); } catch { setComparison(null); }
  }, [patientId, planDate]);

  const fetchStats = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/api/patients/${patientId}/diary-stats?days=${statsDays}`); setStats(data); } catch { setStats(null); }
  }, [patientId, statsDays]);

  const fetchCompliance = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/api/patients/${patientId}/diary-compliance-summary?days=7`); setComplianceSummary(data); } catch { setComplianceSummary(null); }
  }, [patientId]);

  useEffect(() => { fetchTimeline(); fetchCalendar(); fetchDiary(); }, [fetchTimeline, fetchCalendar, fetchDiary]);
  useEffect(() => { if (view === 'plano') { fetchComparison(); fetchStats(); fetchCompliance(); } }, [view, fetchComparison, fetchStats, fetchCompliance]);

  const handleLike = async (item, liked) => {
    const t = item._record_type;
    try {
      if (t === 'alimentar') await axios.put(`${API}/api/diary/${item.id}/nutri-feedback`, { liked });
      else if (t === 'agua') await axios.put(`${API}/api/water-logs/${item.date}/nutri-feedback`, { patient_id: patientId, liked });
      else if (t === 'atividade') await axios.put(`${API}/api/activities/${item.id}/nutri-feedback`, { liked });
      else if (t === 'peso') await axios.put(`${API}/api/weight-records/${item.id}/nutri-feedback`, { liked });
      fetchTimeline();
    } catch {}
  };
  const handleComment = async (item, comment) => {
    const t = item._record_type;
    try {
      if (t === 'alimentar') await axios.put(`${API}/api/diary/${item.id}/nutri-feedback`, { comment });
      else if (t === 'agua') await axios.put(`${API}/api/water-logs/${item.date}/nutri-feedback`, { patient_id: patientId, comment });
      else if (t === 'atividade') await axios.put(`${API}/api/activities/${item.id}/nutri-feedback`, { comment });
      else if (t === 'peso') await axios.put(`${API}/api/weight-records/${item.id}/nutri-feedback`, { comment });
      toast.success('Comentário guardado'); fetchTimeline();
    } catch {}
  };

  const CAL_COLORS = { verde: 'bg-brand-green', laranja: 'bg-amber-400', vermelho: 'bg-red-400', cinzento: 'bg-gray-200' };

  const shiftPlanDate = (delta) => {
    const d = new Date(planDate); d.setDate(d.getDate() + delta);
    setPlanDate(d.toISOString().slice(0, 10));
  };

  // Calendar grid
  const renderCalendar = () => {
    const [y, m] = calMonth.split('-').map(Number);
    const firstDay = new Date(y, m - 1, 1).getDay();
    const daysInMonth = new Date(y, m, 0).getDate();
    const offset = firstDay === 0 ? 6 : firstDay - 1;
    const cells = [];
    for (let i = 0; i < offset; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);
    return (
      <div>
        <div className="flex items-center justify-between mb-3">
          <button onClick={() => { const d = new Date(y, m - 2, 1); setCalMonth(d.toISOString().slice(0, 7)); }} className="text-brand-green text-sm">&larr;</button>
          <span className="text-sm font-medium text-brand-text">{calMonth}</span>
          <button onClick={() => { const d = new Date(y, m, 1); setCalMonth(d.toISOString().slice(0, 7)); }} className="text-brand-green text-sm">&rarr;</button>
        </div>
        <div className="grid grid-cols-7 gap-1 text-center text-[9px] text-brand-muted mb-1">
          {['Seg','Ter','Qua','Qui','Sex','Sáb','Dom'].map(d => <div key={d}>{d}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {cells.map((day, i) => {
            if (!day) return <div key={i} />;
            const dateStr = `${calMonth}-${String(day).padStart(2, '0')}`;
            const color = calendar[dateStr] || 'cinzento';
            return (
              <div key={i} className="aspect-square flex items-center justify-center relative" title={`${dateStr}: ${color}`}>
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] text-white ${CAL_COLORS[color]}`}>{day}</div>
              </div>
            );
          })}
        </div>
        <div className="flex gap-3 justify-center mt-3 text-[9px] text-brand-muted">
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-brand-green" /> Cumpriu</span>
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-amber-400" /> Parcial</span>
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-red-400" /> Não cumpriu</span>
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-gray-200" /> Sem registo</span>
        </div>
      </div>
    );
  };

  return (
    <div data-testid="tab-diario">
      {/* View toggle */}
      <div className="flex gap-2 mb-4">
        <button onClick={() => setView('plano')} className={`flex-1 py-2 rounded-xl text-xs font-medium ${view === 'plano' ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted border border-brand-border'}`} data-testid="diario-view-plano">Plano vs Diário</button>
        <button onClick={() => setView('timeline')} className={`flex-1 py-2 rounded-xl text-xs font-medium ${view === 'timeline' ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted border border-brand-border'}`} data-testid="diario-view-timeline">Timeline</button>
        <button onClick={() => setView('calendar')} className={`flex-1 py-2 rounded-xl text-xs font-medium ${view === 'calendar' ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted border border-brand-border'}`} data-testid="diario-view-calendar">Calendário</button>
      </div>

      {view === 'plano' ? (
        <PlanoVsDiarioView
          comparison={comparison}
          stats={stats}
          planDate={planDate}
          setPlanDate={setPlanDate}
          shiftPlanDate={shiftPlanDate}
          statsDays={statsDays}
          setStatsDays={setStatsDays}
          complianceSummary={complianceSummary}
        />
      ) : view === 'calendar' ? (
        <div className="bg-white border border-brand-border rounded-2xl p-4">
          {renderCalendar()}
        </div>
      ) : (
        <>
          {/* Filters */}
          <div className="flex gap-1.5 mb-3 flex-wrap">
            {[{v:'',l:'Todos'},{v:'alimentar',l:'Alimentar'},{v:'agua',l:'Água'},{v:'atividade',l:'Atividade'},{v:'peso',l:'Peso'}].map(t => (
              <button key={t.v} onClick={() => setRecordType(t.v)} className={`px-2.5 py-1 rounded-full text-[10px] font-medium ${recordType === t.v ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted border border-brand-border'}`}>{t.l}</button>
            ))}
            <button onClick={() => setValidated(validated === 'nao' ? '' : 'nao')} className={`px-2.5 py-1 rounded-full text-[10px] font-medium ${validated === 'nao' ? 'bg-amber-500 text-white' : 'bg-brand-bg text-brand-muted border border-brand-border'}`}>Não validados</button>
          </div>
          <div className="flex gap-2 mb-4">
            <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="rounded-xl border-brand-border text-xs flex-1" placeholder="De" />
            <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="rounded-xl border-brand-border text-xs flex-1" placeholder="Até" />
          </div>

          {/* Timeline */}
          <p className="text-[10px] text-brand-muted mb-2">{timeline.length} registo{timeline.length !== 1 ? 's' : ''}</p>
          {timeline.length === 0 ? <p className="text-center text-brand-muted py-8">Sem registos.</p> : (
            <div className="space-y-2">
              {timeline.map(item => (
                <TimelineCard key={item.id || item.date + item._record_type} item={item} onLike={handleLike} onComment={handleComment} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function PlanoVsDiarioView({ comparison, stats, planDate, setPlanDate, shiftPlanDate, statsDays, setStatsDays, complianceSummary }) {
  if (!comparison) return <div className="text-center py-8 text-brand-muted text-sm">A carregar...</div>;
  const { plan_name, planned_meals = [], unplanned_entries = [], daily_summary = {}, water_goal, water_today, adherence_pct = 0, counts = {} } = comparison;
  const series = (stats?.series || []).map(s => ({ ...s, satiety_avg: s.satiety_avg ?? null, hunger_avg: s.hunger_avg ?? null }));
  const hasSeriesData = series.some(s => s.satiety_avg != null || s.symptom_count > 0);
  const overallTone = adherence_pct >= 70 ? 'bg-brand-green' : adherence_pct >= 40 ? 'bg-amber-400' : 'bg-red-400';
  const hasSevereAlert = planned_meals.some(pm => (pm.entries || []).some(e => (e.symptoms || []).includes('reacao_alergica') || e.baby_reaction === 'reacao_alergica' || e.reaction === 'reacao_alergica'));

  return (
    <div className="space-y-4" data-testid="diario-plano-view">
      {/* Date nav + adherence card */}
      <div className="bg-white border border-brand-border rounded-2xl p-3">
        <div className="flex items-center justify-between gap-2 mb-3">
          <button onClick={() => shiftPlanDate(-1)} className="text-brand-green text-xs px-2 py-1" data-testid="diario-prev-day">← dia ant.</button>
          <Input type="date" value={planDate} onChange={e => setPlanDate(e.target.value)} className="rounded-xl border-brand-border text-xs max-w-[160px]" data-testid="diario-date-picker" />
          <button onClick={() => shiftPlanDate(1)} className="text-brand-green text-xs px-2 py-1" data-testid="diario-next-day">dia seg. →</button>
        </div>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[10px] text-brand-muted">{plan_name || 'Sem plano ativo'}</p>
            <p className="text-2xl font-heading text-brand-text" data-testid="diario-adherence-pct">{adherence_pct}%</p>
            <p className="text-[10px] text-brand-muted">adesão · {counts.logged || 0} reg · {counts.skipped || 0} saltou · {counts.missing || 0} sem reg</p>
          </div>
          <div className={`w-12 h-12 rounded-full ${overallTone}`} />
        </div>
      </div>

      {complianceSummary && complianceSummary.total > 0 && (
        <div className="bg-white border border-brand-border rounded-2xl p-3" data-testid="diario-compliance-summary">
          <p className="text-[10px] text-brand-muted mb-2">Autoavaliação de cumprimento · últimos 7 dias</p>
          <div className="flex gap-2 flex-wrap">
            {['cumpri', 'parcial', 'nao_cumpri'].map(k => (
              <span key={k} className={`flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-full ${COMPLIANCE_PILL[k]}`} data-testid={`compliance-count-${k}`}>
                <span className={`w-2 h-2 rounded-full ${COMPLIANCE_DOT[k]}`} /> {COMPLIANCE_LABEL[k]}: <strong>{complianceSummary.counts[k] || 0}</strong>
              </span>
            ))}
          </div>
        </div>
      )}

      {hasSevereAlert && (
        <div className="bg-red-50 border border-red-300 rounded-2xl p-3 flex items-start gap-2" data-testid="diario-severe-alert">
          <Warning size={18} weight="fill" className="text-red-500 mt-0.5" />
          <div>
            <p className="text-xs font-semibold text-red-700">Atenção: sintoma grave / reação alérgica registado neste dia.</p>
            <p className="text-[10px] text-red-600">Já foi enviado alerta à nutricionista responsável.</p>
          </div>
        </div>
      )}

      {/* Daily summary */}
      {(daily_summary.mood || daily_summary.water_glasses != null || daily_summary.weight_kg || daily_summary.notes) && (
        <div className="bg-white border border-brand-border rounded-2xl p-3" data-testid="diario-daily-summary">
          <p className="text-[10px] text-brand-muted mb-2">Resumo diário</p>
          <div className="grid grid-cols-2 gap-2 text-xs text-brand-text">
            {daily_summary.mood ? <div>Humor: <span className="text-lg">{MOOD_EMOJI[Math.max(0, Math.min(4, daily_summary.mood - 1))]}</span></div> : null}
            <div>Água: {water_today}/{water_goal}</div>
            {daily_summary.weight_kg ? <div>Peso: {daily_summary.weight_kg} kg</div> : null}
            {daily_summary.notes ? <div className="col-span-2 text-brand-muted italic">"{daily_summary.notes}"</div> : null}
          </div>
        </div>
      )}

      {/* Side-by-side meals */}
      {planned_meals.length === 0 ? (
        <div className="bg-white border border-brand-border rounded-2xl p-6 text-center text-brand-muted text-sm">Sem plano alimentar ativo para este dia.</div>
      ) : (
        <div className="space-y-2">
          {planned_meals.map(pm => (
            <div key={pm.type} className={`border rounded-2xl p-3 ${ADH_BG[pm.adherence_status] || 'bg-white border-brand-border'}`} data-testid={`diario-meal-${pm.type}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${ADH_DOT[pm.adherence_status]}`} />
                  <p className="text-xs font-medium text-brand-text">{pm.label}</p>
                  {pm.time && <span className="text-[10px] text-brand-muted">{pm.time}</span>}
                </div>
                <span className="text-[9px] uppercase tracking-wide text-brand-muted">{ADH_LABEL[pm.adherence_status]}</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-[10px] text-brand-muted mb-1">Planeado</p>
                  {(pm.foods || []).length === 0 ? (
                    <p className="text-[11px] text-brand-placeholder italic">-</p>
                  ) : (
                    <ul className="text-[11px] text-brand-text space-y-0.5">
                      {(pm.foods || []).map((f, i) => <li key={i}>• {f}</li>)}
                    </ul>
                  )}
                </div>
                <div>
                  <p className="text-[10px] text-brand-muted mb-1">Registado</p>
                  {(pm.entries || []).length === 0 ? (
                    <p className="text-[11px] text-red-500 italic">Sem registo</p>
                  ) : (pm.entries || []).map(e => (
                    <div key={e.id} className="text-[11px] text-brand-text mb-1.5">
                      <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
                        {e.meal_time && <span className="text-[10px] text-brand-muted" data-testid={`diario-entry-time-${e.id}`}>{e.meal_time}</span>}
                        {e.compliance && <span className={`text-[9px] px-1.5 py-0.5 rounded-full flex items-center gap-1 ${COMPLIANCE_PILL[e.compliance]}`} data-testid={`diario-entry-compliance-${e.id}`}><span className={`w-1.5 h-1.5 rounded-full ${COMPLIANCE_DOT[e.compliance]}`} />{COMPLIANCE_LABEL[e.compliance]}</span>}
                      </div>
                      {e.skipped ? (
                        <p className="text-amber-600">Saltou {e.skip_reason ? `· ${e.skip_reason}` : ''}</p>
                      ) : (
                        <>
                          {e.description && <p>{e.description}</p>}
                          <div className="flex gap-2 flex-wrap mt-0.5 text-[10px] text-brand-muted">
                            {e.hunger_before != null && <span>Fome antes: {e.hunger_before}/5</span>}
                            {e.satiety_after != null && <span>Saciedade: {e.satiety_after}/5</span>}
                            {e.texture && <span>Textura: {TEXTURE_LABELS[e.texture] || e.texture}</span>}
                            {e.baby_reaction && <span className={e.baby_reaction === 'reacao_alergica' ? 'text-red-600 font-medium' : ''}>{BABY_REACTION_LABELS[e.baby_reaction] || e.baby_reaction}</span>}
                          </div>
                          {(e.symptoms || []).length > 0 && (
                            <div className="flex gap-1 flex-wrap mt-0.5">
                              {(e.symptoms || []).map(s => (
                                <span key={s} className={`text-[9px] px-1.5 py-0.5 rounded-full ${s === 'reacao_alergica' ? 'bg-red-100 text-red-700 font-semibold' : 'bg-brand-bg text-brand-muted'}`}>{SYMPTOM_LABELS[s] || s}</span>
                              ))}
                            </div>
                          )}
                          {e.note && <p className="text-[10px] text-brand-muted italic mt-0.5">"{e.note}"</p>}
                          {e.photo_path && <img src={`${API}/api/files/${e.photo_path}`} alt="" className="mt-1 rounded-lg max-h-24" />}
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
          {unplanned_entries.length > 0 && (
            <div className="border border-dashed border-brand-border rounded-2xl p-3 bg-white" data-testid="diario-unplanned">
              <p className="text-[10px] text-brand-muted mb-1">Registos fora do plano</p>
              {unplanned_entries.map(e => (
                <p key={e.id} className="text-[11px] text-brand-text">• {e.meal_type}: {e.description || (e.skipped ? 'saltou' : '-')}</p>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Chart */}
      <div className="bg-white border border-brand-border rounded-2xl p-3" data-testid="diario-stats-chart">
        <div className="flex items-center justify-between mb-2">
          <p className="text-[11px] text-brand-muted">Evolução · saciedade média e sintomas</p>
          <select value={statsDays} onChange={e => setStatsDays(parseInt(e.target.value))} className="text-[10px] border border-brand-border rounded-lg px-2 py-1" data-testid="diario-stats-days">
            <option value={7}>7 dias</option>
            <option value={14}>14 dias</option>
            <option value={30}>30 dias</option>
            <option value={60}>60 dias</option>
          </select>
        </div>
        {!hasSeriesData ? (
          <p className="text-center text-brand-muted text-xs py-8">Sem dados suficientes.</p>
        ) : (
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={series} margin={{ top: 5, right: 10, bottom: 0, left: -20 }}>
              <CartesianGrid stroke="#E8E5DE" strokeDasharray="3 3" />
              <XAxis dataKey="date" tick={{ fontSize: 9 }} tickFormatter={d => d.slice(5)} />
              <YAxis yAxisId="l" domain={[0, 5]} tick={{ fontSize: 9 }} />
              <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 9 }} />
              <RTooltip contentStyle={{ fontSize: 11 }} />
              <Legend wrapperStyle={{ fontSize: 10 }} />
              <Line yAxisId="l" type="monotone" dataKey="satiety_avg" stroke="#657161" strokeWidth={2} name="Saciedade" connectNulls dot={{ r: 2 }} />
              <Line yAxisId="r" type="monotone" dataKey="symptom_count" stroke="#C2410C" strokeWidth={2} name="Sintomas" dot={{ r: 2 }} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}

function TimelineCard({ item, onLike, onComment }) {
  const [liked, setLiked] = useState(!!item.nutri_liked);
  const [comment, setComment] = useState(item.nutri_comment || '');
  const [showComment, setShowComment] = useState(false);
  const TYPE_EMOJI = { alimentar: '🍽️', agua: '💧', atividade: '🏃', peso: '⚖️' };
  const getDesc = () => {
    switch (item._record_type) {
      case 'alimentar': return item.description || item.meal_type || 'Registo alimentar';
      case 'agua': return `${item.glasses || 0} copos de água`;
      case 'atividade': return `${item.type || 'Atividade'} - ${item.duration_min || 0} min`;
      case 'peso': return `${item.peso || '?'} kg`;
      default: return '';
    }
  };
  return (
    <div className={`bg-white border rounded-2xl p-3 ${item._validated ? 'border-brand-green/30' : 'border-brand-border'}`}>
      <div className="flex items-start gap-2">
        <span className="text-lg">{TYPE_EMOJI[item._record_type]}</span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-brand-muted">{fmtDatePt(item.date)}</span>
            {item._validated && <span className="text-[8px] bg-brand-green/10 text-brand-green px-1.5 py-0.5 rounded-full">Validado</span>}
            {item.edited && <span className="text-[8px] text-brand-placeholder italic">(editado)</span>}
          </div>
          <p className="text-xs text-brand-text mt-0.5">{getDesc()}</p>
          {item._record_type === 'alimentar' && (() => {
            const hasFome = item.hunger_before != null && item.hunger_before !== '';
            const hasSac = item.satiety_after != null && item.satiety_after !== '';
            const syms = Array.isArray(item.symptoms) ? item.symptoms : [];
            const note = item.note;
            if (!hasFome && !hasSac && !note && syms.length === 0) return null;
            return (
              <div className="mt-1 space-y-1" data-testid="timeline-diary-details">
                {(hasFome || hasSac) && (
                  <div className="flex gap-1.5 flex-wrap">
                    {hasFome && <span className="text-[9px] bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded-full" data-testid="timeline-hunger">Fome antes: {item.hunger_before}/5</span>}
                    {hasSac && <span className="text-[9px] bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded-full" data-testid="timeline-satiety">Saciedade: {item.satiety_after}/5</span>}
                  </div>
                )}
                {syms.length > 0 && (
                  <div className="flex gap-1 flex-wrap" data-testid="timeline-symptoms">
                    {syms.map(s => <span key={s} className="text-[9px] bg-red-50 text-red-600 px-1.5 py-0.5 rounded-full">{s.replace(/_/g, ' ')}</span>)}
                  </div>
                )}
                {note && <p className="text-[10px] text-brand-muted italic" data-testid="timeline-patient-note">"{note}"</p>}
              </div>
            );
          })()}
          {item.nutri_comment && (
            <div className="mt-1.5 bg-brand-green/5 border-l-2 border-brand-green rounded px-2 py-1" data-testid="timeline-nutri-note">
              <span className="text-[9px] text-brand-green font-semibold uppercase tracking-wide">Nutri</span>
              <p className="text-[10px] text-brand-green mt-0.5">{item.nutri_comment}</p>
            </div>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 mt-2 pt-2 border-t border-brand-border/30">
        <button onClick={async () => { const v = !liked; setLiked(v); await onLike(item, v); }} className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] ${liked ? 'bg-brand-gold/15 text-brand-gold-hover' : 'bg-brand-bg text-brand-muted'}`}>
          <Heart size={10} weight={liked ? 'fill' : 'regular'} /> Gosto
        </button>
        <button onClick={() => setShowComment(!showComment)} className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] ${comment ? 'bg-brand-green/10 text-brand-green' : 'bg-brand-bg text-brand-muted'}`}>
          <ChatCircle size={10} /> Comentar
        </button>
      </div>
      {showComment && (
        <Input value={comment} onChange={e => setComment(e.target.value)} onBlur={async () => { await onComment(item, comment); }} onKeyDown={e => { if (e.key === 'Enter') onComment(item, comment); }} placeholder="Comentário..." className="mt-2 rounded-xl border-brand-border text-xs" />
      )}
    </div>
  );
}

export default DiárioTab;
